import { ReactNode } from 'react';
import {
    TbEdit,
    TbEye,
    TbLayersIntersect,
    TbMessageCircle,
    TbShare,
    TbShieldCheck,
    TbUser,
    TbUserCheck,
    TbUserCircle,
    TbUserHexagon,
    TbUserPause,
    TbUserPentagon,
    TbUserQuestion,
    TbUsers,
    TbUserSquare,
    TbUserX,
} from 'react-icons/tb';



import {
    MemberPermissionType,
    MemberStatusType,
    PresenceStatus,
} from '@/types';

import type { MapToken } from './types';

export const createWorkspacePermissionMaps = (token: MapToken) => {
    const workspaceResourceVisibilityTypeToIconMap = {
        Personal: <TbUser size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
        Team: <TbUsers size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
        Connected: (
            <TbLayersIntersect
                size={'1rem'}
                style={{
                    verticalAlign: '-0.2rem',
                    marginRight: '0.375rem',
                }}
            />
        ),
        Shared: <TbShare size={'1rem'} style={{ marginRight: '0.375rem', verticalAlign: '-0.2rem' }} />,
    } as Record<string, ReactNode>;
    
    const workspaceResourceMaps = { ...workspaceResourceVisibilityTypeToIconMap };
    
    const mapStatusToAntBadgeProps = {
        online: 'success',
        away: 'warning',
        offline: 'default',
    } as Record<PresenceStatus, 'success' | 'warning' | 'default' | 'processing' | 'error' | undefined>;
    
    const mapStatusToAntBadgeColor = {
        online: token.green6,
        away: token.yellow6,
        offline: token.colorBgLayout,
    } as Record<PresenceStatus, string>;
    
    const mapStatusToWorkspaceStatus = {
        online: 'Online',
        away: 'Away',
        offline: 'Offline',
    } as Record<PresenceStatus, string>;
    
    const mapActivityToAntBadgeProps: Record<
        string,
        'success' | 'warning' | 'default' | 'processing' | 'error' | undefined
    > = {
        'resource:view': 'success',
        'resource:edit': 'success',
        idle: 'warning',
    };
    
    const mapActivityToWorkspaceResourceStatus: Record<string, string> = {
        'resource:view': 'Viewing',
        'resource:edit': 'Editing',
        idle: 'Idle in',
    };
    
    const colorMaps = {
        mapStatusToAntBadgeProps,
        mapStatusToAntBadgeColor,
        mapStatusToWorkspaceStatus,
        mapActivityToAntBadgeProps,
        mapActivityToWorkspaceResourceStatus,
    };
    
    const workspacePermissionStatusIconMap = {
        [MemberStatusType.ACTIVE]: <TbUserCheck />,
        [MemberStatusType.PENDING]: <TbUserQuestion />,
        [MemberStatusType.DEACTIVATED]: <TbUserPause />,
        [MemberStatusType.BLOCKED]: <TbUserX />,
    } as Record<MemberStatusType, ReactNode>;
    
    const workspacePermissionIconMap = {
        [MemberPermissionType.OWNER]: <TbUserCircle />,
        [MemberPermissionType.ADMIN]: <TbUserPentagon />,
        [MemberPermissionType.MEMBER]: <TbUserHexagon />,
        [MemberPermissionType.GUEST]: <TbUserSquare />,
        [MemberPermissionType.CUSTOM]: <TbShieldCheck />,
    } as Record<MemberPermissionType, ReactNode>;
    
    const workspacePermissionOrder: Record<MemberPermissionType, number> | any = {
        [MemberPermissionType.OWNER]: 0,
        [MemberPermissionType.ADMIN]: 1,
        [MemberPermissionType.MEMBER]: 2,
        [MemberPermissionType.GUEST]: 3,
        [MemberPermissionType.CUSTOM]: 4,
    };
    
    const resourcePermissionMap = {
        Edit: 'Editor',
        View: 'Viewer',
        Comment: 'Commentator',
        Owner: 'Owner',
    } as Record<string, string>;
    
    const resourcePermissionIconMap = {
        Edit: <TbEdit />,
        View: <TbEye />,
        Comment: <TbMessageCircle />,
        Owner: <TbUserCircle />,
    } as Record<string, ReactNode>;
    
    const permissionMaps = {
        workspacePermissionStatusIconMap,
        workspacePermissionIconMap,
        workspacePermissionOrder,
        resourcePermissionMap,
        resourcePermissionIconMap,
    };

    return {
        ...workspaceResourceMaps,
        ...colorMaps,
        ...permissionMaps,
    };
};
